import * as React from 'react';
import {BaseRouteProps, EditProps, EditState} from '../../interfaces';
import {Organization, Team, UserPermissions} from '../../models';
import {OrganizationDataService, TeamDataService} from '../../services';
import {NavLink} from 'react-router-dom';
import {TeamForm} from './TeamForm';
import {LoadingIndicator, PVAPage} from '../../components';
import {UserStore} from '../../stores';
import { Roles } from '../../stores/Constants';
import {inject, observer} from 'mobx-react';
import {computed} from 'mobx';
import './TeamEdit.css'

// import {BatchFunctions} from "../../constants/Permissions";

interface TeamEditState extends EditState<Team> {
    organizations: Organization[];
    loading:boolean;
    userBatchUploadFile:string;
    userBatchUploadResult:null | {
        fileError:string | undefined,
        recordsUploaded:number,
        recordsInserted:number,
        errors:[{
            recordNumber:number,
            userBatchRecord: {
                lastName: string,
                firstName: string,
                email: string
            },
            errorDescription:string
    }] };
}

interface TeamEditProps extends BaseRouteProps<EditProps> {
    UserStore?: UserStore;
}

@inject('UserStore') @observer
export class TeamEdit extends React.Component<TeamEditProps, TeamEditState> {
    private teamDataService = new TeamDataService(this.props.notificationService);
    private organizationDataService = new OrganizationDataService(this.props.notificationService);

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    @computed get canUseBatchFunctions() : boolean {
        return this.props.UserStore!.checkUserPermission(UserPermissions.BatchFunctions);
    }

    @computed get userEmail() {
        return this.props.UserStore!.userInfo.email;
    }

    @computed get organization() {
        return this.props.UserStore!.userInfo.organization;
    }

    constructor(props: TeamEditProps) {
        super(props);

        this.state = {
            originalEntity: undefined,
            organizations: new Array<Organization>(),
            loading: false,
            userBatchUploadFile: '',
            userBatchUploadResult: null,
        };

        this.save = this.save.bind(this);
    }

    public async componentDidMount() {
        await this.reloadPageData();
    }

    public async reloadPageData() {
        const id = this.props.match.params.id;

        if (id) {
            const team = await this.teamDataService.get(id);
            this.setState({ originalEntity: team });
        }

        if (this.props.UserStore!.userRoleIn([Roles.OrgAdmin, Roles.Manager, Roles.User])) {
            const organization = await this.organizationDataService.get(this.organization!.id);
            this.setState({ organizations: [organization] });
        } else {
            const organizations = await this.organizationDataService.getAll();
            this.setState({ organizations: organizations });
        }
    }

    public render() {
        if (!this.state.originalEntity || this.state.loading) {
            return <LoadingIndicator />;
        }

        return (
            <PVAPage>
                <PVAPage.Header>
                    <PVAPage.Header.Title>
                        <NavLink to={`/team`}>
                            <span className="glyphicon glyphicon-triangle-left"/>
                        </NavLink>
                        &nbsp;
                        Edit Team
                    </PVAPage.Header.Title>
                </PVAPage.Header>

                <PVAPage.Body>
                    <TeamForm defaultValues={this.state.originalEntity} submit={this.save}
                              organizations={this.state.organizations}
                              notificationService={this.props.notificationService}/>
                    <hr/>

                    {this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin, Roles.PonderaAssociate]) &&
                        <button className="btn btn-success" onClick={(e) => this.midranges()}><span
                            className="glyphicon glyphicon-asterisk"/> Calculate Mid Ranges</button>}
                    <hr/>
                    {this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin, Roles.PonderaAssociate]) &&
                        <button className="btn btn-success" onClick={(e) => this.teamReport()}><span
                            className="glyphicon glyphicon-asterisk"/> Re-Calculate Team Report</button>}
                    <hr/>
                    {this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin]) &&
                        <div>
                            <button id={'getTeamScoresReportButton'} className="btn btn-success"
                                    onClick={(e) => this.getTeamScoresReport()}>Download team scores report
                            </button>
                            <label htmlFor={'getTeamScoresReportButton'}>Use this option to compile team scores
                                immediately, and download the report when it's ready. This can take several minutes
                                to complete. Leaving or refreshing this page will stop the report from
                                processing.</label>
                        </div>
                    }
                    <hr/>
                    {this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin]) &&
                        <div>
                            <button id={'queueTeamScoresReportButton'} className="btn btn-success"
                                    onClick={(e) => this.queueTeamScoresReport()}>Get email with team scores report
                            </button>
                            <label htmlFor={'queueTeamScoresReportButton'}>Use this option to add the team scores
                                report to a report queue. You can leave or refresh this page while the report is
                                queued, and it will be emailed to {this.userEmail} when it's ready.</label>
                        </div>
                    }
                    <hr/>
                    { this.canUseBatchFunctions &&
                        <div>
                            <div>
                                <input type="file" className="form-control" onChange={this.selectFile}/>
                                <button id={'uploadUserBatchButton'} className="btn btn-success"
                                        disabled={!this.state.userBatchUploadFile}
                                        onClick={e => this.uploadUsersCsv()}>Upload users
                                </button>
                                <label htmlFor={'uploadUserBatchButton'}>Upload a CSV or Excel file to add new users to this
                                    team</label>
                            </div>
                            <div>
                                <a href={"/files/example_user_batch.csv"}>Example CSV file</a>
                            </div>
                        </div>
                    }
                    <hr/>
                    <button className="btn btn-danger" onClick={(e) => this.delete()}><span
                        className="glyphicon glyphicon-remove"/> Delete
                    </button>
                </PVAPage.Body>

                <PVAPage.Footer />

                { this.state.userBatchUploadResult &&
                    <div className="modal-container">
                        <div className="row">
                            <div className="col-xs-offset-1 col-xs-10 col-sm-offset-2 col-sm-8">
                                <div className="modal-dialog">
                                    <div className="modal-content" role="document">
                                        <div className="modal-header pva-group-title pondera-modal-header">
                                            <button type="button" className="close pondera-close"
                                                    onClick={(e) => this.closeUploadResultsModal()}>
                                                <span className="glyphicon glyphicon-remove"/>
                                                <span className="sr-only">Close</span>
                                            </button>
                                            <div className="modal-title">
                                                <div className="pondera-modal-sub-title">User upload results</div>
                                                <div className="pondera-modal-title">{this.state.userBatchUploadResult.recordsInserted || 0} users created</div>
                                            </div>
                                        </div>
                                        <div className="modal-body">
                                            <div>
                                                <p>Attempted to upload {this.state.userBatchUploadResult.recordsUploaded || 0} users, {this.state.userBatchUploadResult.recordsInserted || 0} were created.</p>
                                                <div>
                                                    <p>Errors encountered: </p>
                                                    {
                                                        !(this.state.userBatchUploadResult.fileError || this.state.userBatchUploadResult.errors.length > 0)
                                                        &&
                                                        <div>None</div>
                                                    }
                                                    {
                                                        (this.state.userBatchUploadResult.fileError || this.state.userBatchUploadResult.errors.length > 0)
                                                        &&
                                                        <div className="user-batch-upload-error-container">
                                                            { this.state.userBatchUploadResult.fileError && (
                                                                <div>{this.state.userBatchUploadResult.fileError}</div>
                                                            ) }
                                                            { this.state.userBatchUploadResult.errors
                                                                && this.state.userBatchUploadResult.errors.map((error, index) => (
                                                                <div>Line {error.recordNumber}: {error.errorDescription}</div>
                                                            )) }
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button onClick={(e) => this.closeUploadResultsModal()} type="button"
                                                    className="btn btn-default">Close
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </PVAPage>);
    }

    private closeUploadResultsModal = async() => {
        await this.setState({...this.state, userBatchUploadResult: null, loading: true});
        await this.reloadPageData();
        await this.setState({...this.state, loading: false});
    }

    private selectFile = async (e: any) => {
        e.preventDefault();
        await this.setState({...this.state, userBatchUploadFile: e.target.files[0]});
    }

    private uploadUsersCsv = async () => {
        await this.setState({...this.state, loading:true});
        try {
            const result = await this.teamDataService.createUserBatch(this.props.match.params.id!, this.state.userBatchUploadFile);
            if (result) {
                await this.setState({...this.state, userBatchUploadResult: result});
            }
        }
        finally {
            await this.setState({...this.state, loading: false});
        }
    }

    private midranges() {

        this.setState(
            {
                loading: true
            },
            () => {
                this.teamDataService.calculateMidRanges(this.state.originalEntity ? this.state.originalEntity.id : 0).then(
                    () => {
                        this.props.notificationService.addInfo('Finished');
                        this.setState({
                            loading: false
                        });
                    },
                    (err: any) => {
                        this.props.notificationService.addError(err);
                        console.error(err);
                        this.setState({
                            loading: false
                        });
                    }
                );
            });
    }

    private getTeamScoresReport() {
        this.setState({loading: true});

        this.teamDataService.getTeamScoresReport(this.state.originalEntity ? this.state.originalEntity.id : 0).then((reportData: any) => {
            this.setState({
                loading: false
            });
        });
    }

    private async queueTeamScoresReport() {
        this.setState({loading: true});
        await this.teamDataService.queueTeamScoresReport(this.state.originalEntity ? this.state.originalEntity.id : 0);
        this.setState({loading: false});
    }

    private teamReport() {

        this.setState(
            {
                loading: true
            }, 
            () => {
                this.teamDataService.calculateTeamReport(this.state.originalEntity ? this.state.originalEntity.id : 0).then(
                    () => {
                        this.props.notificationService.addInfo('Finished');
                        this.setState({
                            loading: false
                        });
                    },
                    (err: any) => {
                        this.props.notificationService.addError(err);
                        console.error(err);
                        this.setState({
                            loading: false
                        });
                    }
                );
            });
    }

    private delete() {
        const confirmed = confirm(`Are you sure you want to delete ${this.state.originalEntity!.name}?`);

        if (confirmed && this.props.match.params.id) {
            this.teamDataService.delete(this.state.originalEntity!).then(
                () => {
                    this.props.history.push('/team');
                },
                (err) => {
                    console.error(err);
                }
            );
        }
    }

    private save(values: any) {
        const updatedTeam = Object.assign(this.state.originalEntity, values);

        this.teamDataService.update(updatedTeam).then((success) => {
            this.props.history.push('/team');
        });
    }
}


// WEBPACK FOOTER //
// ./src/components/team/TeamEdit.tsx